import { useCallback, useEffect, useRef } from "react";
import type { MessageEvent } from "@veto/domain";
import { destroySessionBundle, type SessionBundle } from "../messaging/accountLifecycle";
import { releaseInstanceLease, setInstanceLostHandler } from "../instanceLock";
import { syncAppBadge } from "../notifications";
import { bindUpdateCheckpoint } from "../pwaUpdateGate";
import { useVaultAutoLock } from "./useVaultAutoLock";

type Screen = "loading" | "onboarding" | "lock" | "app" | "error";

/** Lock clears in-memory vault material; auto-lock + SW update gate while unlocked. */
export function useVaultLockControls(opts: {
  screen: Screen;
  session: SessionBundle | null;
  setVaultKeyB64: (v: string | null) => void;
  setSession: (s: SessionBundle | null) => void;
  setMessages: (m: MessageEvent[]) => void;
  setActiveId: (id: string | null) => void;
  setDraft: (d: string) => void;
  setReplyTo: (m: MessageEvent | null) => void;
  setEditing: (m: MessageEvent | null) => void;
  setShowDetails: (v: boolean) => void;
  setShowSettings: (v: boolean) => void;
  setShowAdd: (v: boolean) => void;
  setBanner: (b: null) => void;
  setScreen: (s: Screen) => void;
  /** SEC: caller revokes decrypted blob URLs (voice notes) + any extra in-memory plaintext. */
  onLocked?: () => void;
}): { lockVault: () => void } {
  // Always free the *current* bundle even though lockVault identity is stable.
  const sessionRef = useRef<SessionBundle | null>(opts.session);
  sessionRef.current = opts.session;
  const lockedRef = useRef(opts.onLocked);
  lockedRef.current = opts.onLocked;
  const lockVault = useCallback(() => {
    // SEC: free WASM identity + ratchet sessions BEFORE dropping React refs.
    try {
      destroySessionBundle(sessionRef.current);
    } catch {
      /* fail-closed to locked screen regardless */
    }
    opts.setVaultKeyB64(null);
    opts.setSession(null);
    opts.setMessages([]);
    opts.setActiveId(null);
    opts.setDraft("");
    opts.setReplyTo(null);
    opts.setEditing(null);
    opts.setShowDetails(false);
    opts.setShowSettings(false);
    opts.setShowAdd(false);
    opts.setBanner(null);
    opts.setScreen("lock");
    releaseInstanceLease();
    void syncAppBadge(0);
    try {
      lockedRef.current?.();
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useVaultAutoLock({ screen: opts.screen, onLock: lockVault });

  // Review H3: another window unlocked — finish in-flight session writes, then lock.
  useEffect(() => {
    setInstanceLostHandler(async () => {
      try {
        await sessionRef.current?.store.drain();
      } catch {
        /* lock regardless */
      }
      lockVault();
    });
    return () => setInstanceLostHandler(null);
  }, [lockVault]);

  useEffect(() => {
    if (opts.screen !== "app") return;
    return bindUpdateCheckpoint();
  }, [opts.screen]);

  return { lockVault };
}
